import React, { useCallback, useMemo, useState } from 'react';
import { CompositeScreenProps, useNavigation } from '@react-navigation/native';
import Constants from 'expo-constants';
import { DrawerNavigationProp } from '@react-navigation/drawer';
import { StackScreenProps } from '@react-navigation/stack';
import { View } from 'react-native';
import { utcToZonedTime } from 'date-fns-tz';
import { addDays, isSameMinute } from 'date-fns';
import { getTimeZone } from 'react-native-localize';
import { useTheme } from 'react-native-paper';
import ActivePaymentMethodRow from './MapView/PaymentMethodRow';
import { useI18n } from '../context/I18nContext';
import ScrollablePage from '../components/ui/ScrollablePage';
import { AppDrawerScreenProp } from '../components/DrawerMenuContent';
import { screenNames } from '../navigators/screenNames';
import { ListItemRow } from '../components/ListItemRow';
import { useMapViewFilter } from '../context/MapViewFilterContext';
import { Currency, ProductSubscriptionStatus } from '../types/appsync-types';
import Typography from '../components/Typography';
import GradientButton from '../components/GradientButton';
import { useActivePayment } from '../payment/MapViewActivePaymentContext';
import Spacer from '../components/Spacer';
import { AuthState, useAuth } from '../context/AuthContext';
import { useGetMyUnpaidReservationsForProductsList, useGetProductById } from '../apis/subscriptionApis';
import Loading from '../components/Loading';
import { PaymentMethodOption } from '../types/misc.types';
import { MapStackRouteParams } from '../navigators/MapStackNavigator';
import DateTimePickerModal from '../components/modal/DateTimePickerModal';
import { useNowMinutes, useToday } from '../hooks/useNow';
import { FilterButton } from '../components/FilterButton';
import { capitalizeFirstLetter, flattenDict, isAfterOrEqual } from '../utils/helpers';
import { setTz } from '../utils/date-utils';
import { SpacePricing } from '../components/ui/SpacePricing';
import { UnlockButton } from '../components/UnlockButton';
import { GetFromStoreButton } from '../components/GetFromStoreButton';
import { useMixpanel } from '../mixpanel/MixpanelContext';
import { useGetMyThirdPartyAuths } from '../apis/appsyncApis';

function SpaceItem(props: { title: string; description: string; spaceId: string }) {
  const { title, description } = props;
  // const navigation = useNavigation<DrawerNavigationProp<any>>();

  // const onPress = useCallback(() => {
  //   navigation.navigate(screenNames.SpaceInfo, {
  //     spaceId,
  //   });
  // }, [navigation, spaceId]);

  return <ListItemRow type="default" title={title} description={description} />;
}

export type LockerReservationDetailRouteParams = {
  productId?: string;
};

type Props = CompositeScreenProps<
  StackScreenProps<MapStackRouteParams, 'LockerReservationDetailScreen'>,
  AppDrawerScreenProp
>;

const MAX_ADVANCE_RESERVE_DAYS = 1;

export const LockerReservationDetailScreen: React.FC<Props> = ({ route }) => {
  const { I18n, currentLocale, formatDate, formatRelative } = useI18n();
  const { colors } = useTheme();
  const mp = useMixpanel();
  const { state, logoutAsGuest } = useAuth();
  const { filter, timeZone, onChange } = useMapViewFilter();
  const todayOnDevice = useToday(getTimeZone());
  const now = useNowMinutes();
  const { activePaymentMethod } = useActivePayment();
  const navigation = useNavigation<DrawerNavigationProp<any>>();
  const [isDateTimePickerVisible, setIsDateTimePickerVisible] = useState<boolean>(false);
  const [dateTimePickerMode, setDateTimePickerMode] = useState<'date' | 'time'>('date');

  const getMyThirdPartyAuthsResult = useGetMyThirdPartyAuths();
  const myThirdPartyAuths = getMyThirdPartyAuthsResult.data?.data?.getMyThirdPartyAuths ?? [];

  const getProductByIdResult = useGetProductById(route.params.productId, true);
  const getUnpaidProductReservations = useGetMyUnpaidReservationsForProductsList();
  const unpaidReservation = getUnpaidProductReservations.data?.data?.getMyUnpaidReservations?.find(
    (reservation) => reservation?.productId === route.params.productId,
  );

  // TODO: Handle product being undefined
  const currentProduct = getProductByIdResult.data?.data?.getProductById;

  const currentSubscription = unpaidReservation ?? currentProduct?.subscription;

  const isSubscriptionActive = !!currentSubscription;
  // TODO: We might want to check here that subscription.status === ACTIVE,
  // however, currently reservation subscriptions' statuses are RESERVED even after we are past the startDate.
  // So depends on if we want to change that logic or not
  const hasReservationStarted =
    currentSubscription?.status === ProductSubscriptionStatus.RESERVED &&
    !!currentSubscription.startDate &&
    isAfterOrEqual(now, new Date(currentSubscription.startDate));
  const isActionRequired =
    currentSubscription?.status === ProductSubscriptionStatus.REQUIRES_ACTION ||
    currentSubscription?.status === ProductSubscriptionStatus.ENDED_NOT_PAID;

  const isUserAuthenticated = currentProduct?.requiredAuth
    ? myThirdPartyAuths.some((auth) => auth?.authProvider === currentProduct.requiredAuth)
    : true;

  const showActions =
    currentSubscription?.status !== ProductSubscriptionStatus.CANCELLED &&
    currentSubscription?.status !== ProductSubscriptionStatus.CANCELLING &&
    currentSubscription?.status !== ProductSubscriptionStatus.PROCESSING &&
    currentSubscription?.status !== ProductSubscriptionStatus.FAILED;

  const spaces = currentProduct?.linkedPods; // This should be only 1 here as its always a single locker
  // TODO: Switch to date format that uses month names for clarity?
  const startDateString = currentSubscription?.startDate
    ? formatDate(new Date(currentSubscription.startDate), 'timeAndDate', timeZone)
    : '';

  let subscriptionStatusText: string | null = null;
  if (isSubscriptionActive) {
    switch (currentSubscription.status) {
      case ProductSubscriptionStatus.CANCELLING:
        subscriptionStatusText = I18n.t('reservation.detail.cancelling');
        break;
      case ProductSubscriptionStatus.REQUIRES_ACTION:
      case ProductSubscriptionStatus.ENDED_NOT_PAID:
        subscriptionStatusText = I18n.t('reservation.detail.actionRequired');
        break;
      case ProductSubscriptionStatus.PROCESSING:
        subscriptionStatusText = I18n.t('reservation.detail.processing');
        break;
      case ProductSubscriptionStatus.FAILED:
        subscriptionStatusText = I18n.t('reservation.detail.failed');
        break;
      default:
        subscriptionStatusText = I18n.t('reservation.detail.activeSince', {
          date: startDateString,
        });
        break;
    }
  }

  const authText: string | null = isUserAuthenticated
    ? null
    : I18n.t(`thirdPartyAuth.thirdPartyAuthRequired.${currentProduct?.requiredAuth}`, {
        defaultValue: I18n.t('thirdPartyAuth.thirdPartyAuthRequired.default', {
          type: I18n.t('thirdPartyAuth.productType.subscription'),
        }),
        type: I18n.t('thirdPartyAuth.productType.subscription'),
      });

  const dateStr = useMemo(() => {
    return capitalizeFirstLetter(
      formatRelative(filter.startDate, todayOnDevice, timeZone, {
        dropDateIfRelative: true,
        showWeekday: false,
        showTime: false,
      }),
    );
  }, [filter.startDate, formatRelative, timeZone, todayOnDevice]);

  const timeStr = useMemo(() => {
    const t = formatDate(filter.startDate, 'time-no-tz', timeZone);
    if (isSameMinute(filter.startDate, now)) {
      return capitalizeFirstLetter(I18n.t('general.now'));
    }
    return t;
  }, [I18n, formatDate, filter.startDate, now, timeZone]);

  const onSetDate = useCallback(
    (newDate: Date) => {
      let validDate;
      if (newDate < now) validDate = now;
      else if (newDate > addDays(now, MAX_ADVANCE_RESERVE_DAYS)) validDate = addDays(now, MAX_ADVANCE_RESERVE_DAYS);
      else validDate = newDate;
      onChange({
        ...filter,
        startDate: validDate,
      });
    },
    [now, filter, onChange],
  );

  const onDateTimeConfirm = useCallback(
    (newDate: Date) => {
      setIsDateTimePickerVisible(false);
      if (newDate) {
        if (dateTimePickerMode === 'date') {
          onSetDate(
            setTz(
              filter.startDate,
              {
                year: newDate.getFullYear(),
                month: newDate.getMonth(),
                date: newDate.getDate(),
              },
              timeZone,
            ),
          );
        } else {
          onSetDate(
            setTz(
              filter.startDate,
              {
                year: newDate.getFullYear(),
                month: newDate.getMonth(),
                date: newDate.getDate(),
                hours: newDate.getHours(),
                minutes: newDate.getMinutes(),
                seconds: 0,
                milliseconds: 0,
              },
              timeZone,
            ),
          );
        }
      }
    },
    [dateTimePickerMode, filter.startDate, onSetDate, timeZone],
  );

  let confirmButtonText: string = '';
  if (isActionRequired) {
    confirmButtonText = I18n.t('reservation.detail.confirmPayment');
  } else {
    confirmButtonText = isSameMinute(filter.startDate, now)
      ? I18n.t('reservation.detail.start')
      : I18n.t('reservation.detail.reserve');
  }

  return (
    <ScrollablePage
      summary={{
        title: currentProduct?.name ?? I18n.t('lockerInfo.reservation.detail.title'),
        description: currentSubscription?.startDate
          ? (currentProduct?.owner?.name ? `${currentProduct.owner.name}\n\n` : '') +
            I18n.t('reservation.detail.activeSince', {
              date: formatDate(new Date(currentSubscription.startDate), 'dateWithYear', timeZone),
            })
          : currentProduct?.owner?.name ?? I18n.t('reservation.detail.summaryDescription'),
        icon: 'clock',
      }}
    >
      {getProductByIdResult.isLoading || getUnpaidProductReservations.isLoading ? (
        <Loading />
      ) : (
        <View>
          <View style={{ marginTop: 20, paddingHorizontal: 20 }}>
            <Typography variant="h3">{I18n.t('lockerInfo.reservation.detail.includedSpacesTitle')}</Typography>
            <Typography variant="body2" style={{ marginVertical: 4 }}>
              {I18n.t('lockerInfo.reservation.detail.includedSpacesDescription')}
            </Typography>
            {spaces?.map((space) => (
              <SpaceItem
                key={space?.localRef}
                spaceId={space?.localRef ?? 'unknown'}
                title={space?.title ?? 'Unknown'}
                description={space?.building?.title ?? ''}
              />
            ))}
          </View>
          <View style={{ marginTop: 20, paddingHorizontal: 20 }}>
            <SpacePricing
              marginSize="none"
              style={{ marginTop: 0, marginBottom: 20 }}
              chargeRates={currentProduct?.chargeRates}
              currency={(currentSubscription?.currency as Currency) || 'EUR'}
              isLoading={getProductByIdResult.isLoading}
            />
            {!isSubscriptionActive && (
              <>
                <Typography variant="h3">{I18n.t('reservation.detail.startingTime')}</Typography>
                <View style={{ flexDirection: 'row', gap: 4, marginVertical: 4 }}>
                  <FilterButton
                    style={{ borderColor: colors.primary, borderWidth: 1 }}
                    icon={'calendar'}
                    title={dateStr}
                    onPress={() => {
                      setIsDateTimePickerVisible(true);
                      setDateTimePickerMode('date');
                    }}
                  />
                  <FilterButton
                    style={{ borderColor: colors.primary, borderWidth: 1 }}
                    icon={'clock'}
                    title={timeStr}
                    onPress={() => {
                      setIsDateTimePickerVisible(true);
                      setDateTimePickerMode('time');
                    }}
                  />
                </View>
              </>
            )}
          </View>
          <View style={{ flexDirection: 'column', marginVertical: 20, paddingHorizontal: 20 }}>
            <Spacer />
            {state === AuthState.GUEST_LOGIN ? (
              <GradientButton
                onPress={() => {
                  logoutAsGuest();
                }}
              >
                {I18n.t('reservation.detail.loginToUse')}
              </GradientButton>
            ) : (
              <View>
                {(!isSubscriptionActive || isActionRequired) && (
                  <ActivePaymentMethodRow
                    paymentMethod={activePaymentMethod}
                    onPress={() => navigation.navigate(screenNames.PaymentChangeBeforeCheckoutView)}
                    statusOnly={!isSubscriptionActive}
                  />
                )}
                {subscriptionStatusText && (
                  <Typography variant="body1" style={{ paddingBottom: 16 }}>
                    {subscriptionStatusText}
                  </Typography>
                )}

                {authText && (
                  <Typography variant="body1" style={{ paddingBottom: 16 }}>
                    {authText}
                  </Typography>
                )}

                {showActions && (
                  <View style={{ flexDirection: 'column', gap: 8 }}>
                    {isUserAuthenticated ? (
                      (!isSubscriptionActive || isActionRequired) && (
                        <GradientButton
                          icon={
                            !isSubscriptionActive
                              ? { name: 'progress-clock', set: 'material-community' }
                              : 'credit-card'
                          }
                          disabled={!isActionRequired && !activePaymentMethod}
                          onPress={() => {
                            navigation.navigate(screenNames.ConfirmReservationProductDialog, {
                              id: currentProduct?.id,
                            });
                          }}
                        >
                          {confirmButtonText}
                        </GradientButton>
                      )
                    ) : (
                      <GradientButton
                        icon={{ name: 'shield-account-outline', set: 'material-community' }}
                        onPress={() => {
                          navigation.navigate(screenNames.ThirdPartyAuthDetail, {
                            authProvider: currentProduct?.requiredAuth,
                          });
                        }}
                      >
                        {I18n.t('thirdPartyAuth.toThirdPartyAuth')}
                      </GradientButton>
                    )}

                    {isSubscriptionActive &&
                      !isActionRequired &&
                      (Constants.manifest?.extra?.disableBitwards ? (
                        <GetFromStoreButton
                          onUnknownStore={() => {
                            navigation.navigate('ChooseStoreDialog');
                          }}
                        >
                          {I18n.t('storeDialog.installToUnlockButton')}
                        </GetFromStoreButton>
                      ) : (
                        <UnlockButton
                          resources={spaces?.[0]?.resources}
                          disabled={!hasReservationStarted}
                          loading={getProductByIdResult.isFetching || getUnpaidProductReservations.isFetching}
                          onPress={() => {
                            mp?.track('Unlock attempt from Locker Detail card', flattenDict('space', spaces?.[0]));
                          }}
                        />
                      ))}

                    {hasReservationStarted && (
                      <GradientButton
                        onPress={() => {
                          navigation.navigate(screenNames.ConfirmReservationProductEndDialog, {
                            id: currentSubscription.id,
                          });
                        }}
                      >
                        {I18n.t('lockerInfo.reservation.detail.endReservation')}
                      </GradientButton>
                    )}
                  </View>
                )}
              </View>
            )}
          </View>
          <DateTimePickerModal
            isVisible={isDateTimePickerVisible}
            mode={dateTimePickerMode}
            date={utcToZonedTime(filter.startDate, timeZone)}
            is24Hour={true}
            locale={currentLocale}
            maximumDate={addDays(now, MAX_ADVANCE_RESERVE_DAYS * 24)}
            minimumDate={utcToZonedTime(now, timeZone)}
            onConfirm={onDateTimeConfirm}
            onCancel={() => {
              setIsDateTimePickerVisible(false);
            }}
          />
        </View>
      )}
    </ScrollablePage>
  );
};
